import { format, parse, isValid } from 'date-fns';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_PREFIX_RE = /^(\d{4}-\d{2}-\d{2})/;

/**
 * Safely extract a YYYY-MM-DD string from any date-like value.
 *
 * Handles:
 * - Moment objects (uses .format() which respects the Moment's UTC/local mode)
 * - Native Date objects (uses local date parts to avoid UTC shift)
 * - Strings (extracts first 10 chars if ISO-like)
 *
 * This is the CORRECT way to serialize a date from a Material datepicker
 * when using MomentDateAdapter with { useUtc: true } globally.
 */
export function extractDateString(value: unknown): string {
  if (!value) return '';

  // Moment object — has .format() and .isValid()
  if (typeof value === 'object' && value !== null && typeof (value as any).format === 'function') {
    const m = value as any;
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
  }

  // Native Date
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return '';
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }

  // String — extract date part
  const str = String(value).trim();
  if (DATE_PREFIX_RE.test(str)) {
    return str.substring(0, 10);
  }

  return '';
}

/**
 * Parse any date-like value into a local-time Date.
 * YYYY-MM-DD strings are parsed as local midnight (not UTC).
 * YYYY-MM-DDTHH:mm:ss strings (no trailing Z) are parsed as local time.
 * SQL-style strings like "Mar  4 2026  8:28PM" are also handled.
 */
export function parseLocalDate(value: unknown): Date {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return new Date(value.getTime());
  const str = String(value).trim();
  if (!str) return new Date(NaN);

  // Pure date: YYYY-MM-DD → local midnight
  if (DATE_ONLY_RE.test(str)) {
    return parse(str, 'yyyy-MM-dd', new Date());
  }

  // ISO datetime WITHOUT trailing Z → treat as local time
  // e.g. "2026-05-06T00:00:00.000" or "2026-05-06T13:30:00"
  if (DATE_PREFIX_RE.test(str) && !str.endsWith('Z')) {
    // Try parsing with milliseconds first, then without
    let d = parse(str, "yyyy-MM-dd'T'HH:mm:ss.SSS", new Date());
    if (isValid(d)) return d;
    d = parse(str, "yyyy-MM-dd'T'HH:mm:ss", new Date());
    if (isValid(d)) return d;
    d = parse(str, "yyyy-MM-dd'T'HH:mm", new Date());
    if (isValid(d)) return d;
  }

  // ISO datetime WITH trailing Z → extract date part, parse as local midnight
  if (DATE_PREFIX_RE.test(str) && str.endsWith('Z')) {
    const datePart = str.match(DATE_PREFIX_RE)![1];
    return parse(datePart, 'yyyy-MM-dd', new Date());
  }

  // SQL Server format: "Mar  4 2026  8:28PM" or normalized
  const normalized = str.replace(/\s+/g, ' ');
  const sqlMatch = normalized.match(
    /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})\s+(\d{1,2}):(\d{2})(AM|PM)$/i,
  );
  if (sqlMatch) {
    const monthMap: Record<string, number> = {
      jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
      jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
    };
    const month = monthMap[sqlMatch[1].toLowerCase()];
    if (month != null) {
      let hour = Number(sqlMatch[4]);
      const minute = Number(sqlMatch[5]);
      const ampm = sqlMatch[6].toUpperCase();
      if (ampm === 'AM' && hour === 12) hour = 0;
      if (ampm === 'PM' && hour < 12) hour += 12;
      return new Date(Number(sqlMatch[3]), month, Number(sqlMatch[2]), hour, minute, 0, 0);
    }
  }

  // Fallback — let Date constructor try
  return new Date(str);
}

/** Format date to MM/DD/YYYY  (e.g. 04/29/2026) */
export function formatDateMDY(d: string | Date | null | undefined): string {
  if (!d) return '';
  const parsed = parseLocalDate(d);
  return isValid(parsed) ? format(parsed, 'MM/dd/yyyy') : '';
}

/** @deprecated Use formatDateMDY instead */
export const formatDateDMY = formatDateMDY;

/** Format date to "Apr 29, 2026" style (MMM DD, YYYY) */
export function formatDateLong(d: Date | string | null): string {
  if (!d) return '';
  const parsed = parseLocalDate(d);
  return isValid(parsed) ? format(parsed, 'MMM dd, yyyy') : String(d);
}

/** Format date to "April 29, 2026" style (full month name) */
export function formatDateFull(d: Date | string | null): string {
  if (!d) return '';
  const parsed = parseLocalDate(d);
  return isValid(parsed) ? format(parsed, 'MMMM d, yyyy') : '';
}

/** Get weekday name (e.g. "Monday") from a date-like value */
export function getDayName(d: Date | string | null): string {
  if (!d) return '';
  const parsed = parseLocalDate(d);
  return isValid(parsed) ? format(parsed, 'EEEE') : '';
}

/** Format date to ISO date string (YYYY-MM-DD) using local time.
 *  Also handles Moment objects (from MomentDateAdapter with useUtc: true)
 *  by calling .format() which respects the Moment's UTC/local mode. */
export function toISODate(d: unknown): string {
  // Handle Moment objects first — avoids timezone shift from Moment → native Date conversion
  if (d && typeof d === 'object' && typeof (d as any).format === 'function') {
    const m = d as any;
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
  }
  const parsed = parseLocalDate(d);
  return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : '';
}
